import { isAxiosError, isCancel } from 'axios';
import type { ApiResponse } from '@/types/api';

export type UserErrorContext = 'request' | 'load' | 'save' | 'export' | 'upload';

export interface ParseUserErrorOptions {
  /** 只影响兜底文案，不改变错误含义或执行重试 */
  context?: UserErrorContext;
  /** 调用方明确观察到的网络状态；未提供时不推断用户离线 */
  online?: boolean;
}

interface UserErrorDetails {
  /** 保留原始对象用于诊断和取消识别，不包装或修改错误 */
  originalError: unknown;
  httpStatus?: number;
  /** 仅来自现有 ApiResponse.code，不与 HTTP 状态码混用 */
  businessCode?: number;
}

export type ParsedUserError = UserErrorDetails &
  (
    | { kind: 'canceled'; message: undefined }
    | {
        kind: 'timeout' | 'network' | 'http' | 'business' | 'user' | 'unknown';
        message: string;
      }
  );

/**
 * 显式标记项目自定义且已审核的用户文案，保留本地校验的业务含义
 * 不应把未经确认的后端 msg 或 SDK 异常直接包装成此类型
 */
export class UserFacingError extends Error {
  /** cause 可关联原始异常，文案与诊断信息分别保留 */
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'UserFacingError';
  }
}

/** 业务失败仍作为 Error 抛出，message 用于展示，原始 code/msg 和 cause 用于诊断 */
export class ApiBusinessError extends Error {
  readonly code: number;
  readonly msg: string;

  /** userMessage 来自统一解析，业务 msg 按后端约定保留，空文案使用上下文兜底 */
  constructor(
    response: Pick<ApiResponse, 'code' | 'msg'>,
    userMessage: string,
    options?: ErrorOptions
  ) {
    super(userMessage, options);
    this.name = 'ApiBusinessError';
    this.code = response.code;
    this.msg = response.msg;
  }
}

const FALLBACK_MESSAGES: Record<UserErrorContext, string> = {
  request: '请求失败，请稍后重试',
  load: '数据加载失败，请稍后重试',
  save: '保存失败，请稍后重试',
  export: '导出失败，请稍后重试',
  upload: '上传失败，请稍后重试',
};

/** 校验现有业务失败结构，避免把文件流、成功响应或不完整数据当作业务错误 */
function getBusinessFailure(value: unknown): Pick<ApiResponse, 'code' | 'msg'> | undefined {
  if (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    typeof value.code === 'number' &&
    Number.isFinite(value.code) &&
    value.code !== 0 &&
    'msg' in value &&
    typeof value.msg === 'string'
  ) {
    return { code: value.code, msg: value.msg };
  }
}

/** 兼容 Axios 和浏览器取消，判断依赖标识而不是异常正文 */
function isCanceledError(error: unknown): boolean {
  if (isCancel(error)) return true;
  if (isAxiosError(error) && error.code === 'ERR_CANCELED') return true;

  return (
    (error instanceof Error ||
      (typeof DOMException !== 'undefined' && error instanceof DOMException)) &&
    error.name === 'AbortError'
  );
}

/** 返回浏览器规范的超时标识，不把任意 TypeError 或包含 timeout 的文案归类为网络错误 */
function isBrowserTimeout(error: unknown): boolean {
  return (
    (error instanceof Error ||
      (typeof DOMException !== 'undefined' && error instanceof DOMException)) &&
    error.name === 'TimeoutError'
  );
}

/**
 * 将请求和运行异常解析为用户文案，无 Toast、日志、跳转、网络请求等副作用
 * 后端已统一保证业务 msg 面向用户，非空文案直接保留，不按错误码或语言做映射
 * 原始运行异常与 HTTP 服务异常仍使用友好兜底，保留 HTTP/业务信息用于诊断
 * 本地校验仅通过 UserFacingError 显式保留；取消结果不提供展示文案
 */
export function parseUserError(
  error: unknown,
  options: ParseUserErrorOptions = {}
): ParsedUserError {
  const context = options.context ?? 'request';
  const fallbackMessage = FALLBACK_MESSAGES[context];
  const axiosError = isAxiosError<unknown>(error) ? error : undefined;
  const businessFailure = getBusinessFailure(axiosError?.response?.data ?? error);
  const details: UserErrorDetails = {
    originalError: error,
    httpStatus: axiosError?.response?.status,
    businessCode: businessFailure?.code,
  };

  if (isCanceledError(error)) {
    return { ...details, kind: 'canceled', message: undefined };
  }

  if (error instanceof UserFacingError && error.message.trim()) {
    return { ...details, kind: 'user', message: error.message };
  }

  // 请求层已完成解析，后续页面再次解析时保留安全文案，不从原始 msg 重新推断语义
  if (error instanceof ApiBusinessError && error.message.trim()) {
    return { ...details, kind: 'business', message: error.message };
  }

  // 先识别传输状态，不能用响应中的技术 msg 覆盖服务异常或超时
  if (
    axiosError?.code === 'ECONNABORTED' ||
    axiosError?.code === 'ETIMEDOUT' ||
    details.httpStatus === 408 ||
    isBrowserTimeout(error)
  ) {
    return {
      ...details,
      kind: 'timeout',
      message: '请求超时，请检查网络或刷新重试',
    };
  }

  if (
    axiosError &&
    !axiosError.response &&
    (axiosError.code === 'ERR_NETWORK' || axiosError.request)
  ) {
    return {
      ...details,
      kind: 'network',
      message:
        options.online === false
          ? '网络连接已断开，请检查网络设置'
          : '网络连接异常，请检查网络或稍后重试',
    };
  }

  const status = details.httpStatus;
  // 仅信任符合统一响应结构的业务失败；网关和服务异常仍按 HTTP 状态兜底
  if (businessFailure && (status === undefined || status < 500)) {
    return {
      ...details,
      kind: 'business',
      message: businessFailure.msg.trim() ? businessFailure.msg : fallbackMessage,
    };
  }

  if (status !== undefined && status >= 400) {
    let message = fallbackMessage;
    if (status === 401) {
      // 仅凭 HTTP 401 不能断言是已登录会话失效，登录错误也可能使用此状态
      message = '身份验证失败，请检查登录信息或重新登录';
    } else if (status === 403) {
      message = '暂无权限进行此操作，请联系管理员';
    } else if (status === 404) {
      message = '请求的资源不存在或已不可用';
    } else if (status >= 500 && status < 600) {
      message = '系统服务器异常，请稍后重试';
    }
    return { ...details, kind: 'http', message };
  }

  return {
    ...details,
    kind: businessFailure ? 'business' : 'unknown',
    message: fallbackMessage,
  };
}
